import React, { useCallback, useState } from "react";
import { Typography, TextField, Grid, FormControl, CircularProgress } from "@mui/material";
import { Box } from "@mui/system";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import NCRFormHeading from '../NCRFormHeading';
import NCRFormLabel from '../NCRFormLabel';
import { useDropzone } from "react-dropzone";
import toast from 'react-hot-toast';
import NCRSelectField from "../NCRSelectField";
import { processFileForUpload } from "../../../utils/imageCompressor";

export default function AdditionalInformation({ content, onChange }) {
  const [isCompressing, setIsCompressing] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onChange({ [name]: value });
  };
  const handleFileChange = (file) => {
    if (!(file instanceof File)) {
      console.warn("Skipped non-file input:", file);
      return;
    }

    if (file) {
      onChange({
        evidence_attachments: file,
        evidence_attachments_file_name: file.name
      });
    }
  };

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      toast.error("Only PDF, JPG, JPEG, and PNG files are allowed.");
      return;
    }

    const file = acceptedFiles[0];
    if (!file) return;

    const fileSizeMB = file.size / (1024 * 1024);

    // If file is over 10MB, reject completely
    if (fileSizeMB > 10) {
      toast.error("File size should not exceed 10MB!");
      return;
    }

    // If file is between 2MB and 10MB, try to compress (only for images)
    if (fileSizeMB > 2) {
      if (!file.type.startsWith('image/')) {
        toast.error("PDF files must be under 2MB. Only images can be compressed.");
        return;
      }

      setIsCompressing(true);
      try {
        const { file: processedFile, wasCompressed } = await processFileForUpload(file, 2, 10);
        if (wasCompressed) {
          toast.success(`Image compressed from ${fileSizeMB.toFixed(1)}MB to ${(processedFile.size / (1024 * 1024)).toFixed(1)}MB`);
        }
        handleFileChange(processedFile);
      } catch (error) {
        toast.error(error.message || "Failed to compress image");
      } finally {
        setIsCompressing(false);
      }
      return;
    }

    // File is under 2MB, use as is
    handleFileChange(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png',]
    },
    multiple: false,
  });

  const handleFileDeleteIcon = () => {
    onChange({ evidence_attachments: "", evidence_attachments_file_name: "" });
  };

  const theme = createTheme({
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&::placeholder": {
              fontSize: "14px",
              fontWeight: 400,
              color: "rgba(84, 84, 84, 1)",
              opacity: 1,
            },
          },
        },
      },
    },
  });

  return (
    <>
      <ThemeProvider theme={theme}>
        <Box>
          <Box sx={{ p: 3 }}>
            <Box>
              <NCRFormHeading tooltipData={
                [
                  { title: "Evidence/Attachments", description: "Option to upload supporting documents, photos, or reports related to the non-conformance." },
                  { title: "Severity of Non-conformance", description: "Dropdown to classify the severity of the issue." },
                  { title: "Responsible Team/Person", description: "Dropdown to select who is responsible for addressing the NCR." },
                  { title: "Corrective Action Required", description: "Dropdown to specify whether corrective action is needed." },
                  { title: "Approving Authority", description: "Name of the admin to be displayed who is responsible for reviewing and approving the NCR." },
                ]
              }>
                Additional information
              </NCRFormHeading>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ mb: 2 }}>
                    <NCRFormLabel sx={{ mb: 1 }}>Evidence/ Attachment:</NCRFormLabel>
                    {content.inputDisable ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img
                          src="/images/pdf-icon.png"
                          alt="NC Pro"
                          style={{ maxWidth: "100%", height: "auto" }}
                        />
                        <NCRFormLabel
                          sx={{
                            px: 1,
                            fontStyle: !content?.formData?.evidence_attachments?.filename ? 'italic' : 'normal',
                            '&:hover': content?.formData?.evidence_attachments?.filename ? { cursor: 'pointer' } : {},
                          }}
                          onClick={() => {
                            if (content.formData.evidence_attachments?.filename) {
                              window.open(`${process.env.REACT_APP_BACKEND_APPLICATION_URL}/uploads/${content.formData?.evidence_attachments?.filename}`, '_blank');
                            }
                          }}
                        >{content.formData.evidence_attachments?.originalname || 'File does not exist'}</NCRFormLabel>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Box
                          {...getRootProps()}
                          sx={{
                            border: "1px dashed rgba(0, 0, 0, 0.3)",
                            py: 1,
                            px: 4,
                            textAlign: "center",
                            cursor: isCompressing ? "wait" : "pointer",
                            borderRadius: "5px",
                            backgroundColor: "rgba(255, 255, 255, 1)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            opacity: isCompressing ? 0.7 : 1,
                          }}
                          onChange={handleFileChange}
                        >
                          <Box sx={{ display: "none" }}>
                            <input {...getInputProps()} disabled={isCompressing} />
                          </Box>
                          {isCompressing ? (
                            <>
                              <CircularProgress size={24} sx={{ mb: 1 }} />
                              <Typography sx={{ fontFamily: 'Lato', fontSize: '14px', fontWeight: 400, color: 'rgba(84, 84, 84, 1)' }}>
                                Compressing image...
                              </Typography>
                            </>
                          ) : (
                            <>
                              <img
                                src="/images/upload-icon.png"
                                alt="NC Pro"
                                style={{ maxWidth: "100%", height: "auto" }}
                              />
                              <Typography
                                sx={{
                                  fontFamily: 'Lato',
                                  fontSize: '14px',
                                  fontWeight: 400,
                                }}>
                                <span style={{ textDecoration: 'underline', color: 'rgba(47, 70, 216, 1)' }}>Browse</span> or <span style={{ color: 'rgba(84, 84, 84, 1)' }}>Drag & drop your files</span>
                              </Typography>
                            </>
                          )}
                        </Box>
                        {content.formData.evidence_attachments_file_name && (
                          <Box sx={{ display: 'flex', alignItems: 'center', pt: 1 }}>
                            <img
                              src="/images/pdf-icon.png"
                              alt="NC Pro"
                              style={{ maxWidth: "100%", height: "auto" }}
                            />
                            <Typography sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 1)', p: 1, }}>
                              {content.formData.evidence_attachments_file_name}
                            </Typography>
                            <img
                              src="/images/file-delete-icon.png"
                              alt="NC Pro"
                              onClick={handleFileDeleteIcon}
                              style={{ maxWidth: "100%", height: "auto", cursor: 'pointer' }}
                            />
                          </Box>
                        )}
                        <NCRFormLabel sx={{ fontSize: '16px', fontWeight: 500, mt: 1 }}>Maximum file size: 10MB</NCRFormLabel>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ mb: 2 }}>
                    <NCRFormLabel isRequired={true} >Corrective action required?</NCRFormLabel>
                    <FormControl>
                      <RadioGroup
                        row
                        aria-labelledby="evidence-radio-label"
                        name="corrective_action_required"
                        onChange={handleInputChange}
                        required
                        value={content.formData.corrective_action_required === "Yes" ? 'Yes' : 'No'}
                      >
                        <FormControlLabel
                          disabled={content.inputDisable || (content.label.includes("edit") && content.formData.implement_interim_containment_action !== null) || (content.label === 'edit' && content.userData?.roleCode !== 'CLTADM')}
                          value="Yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          disabled={content.inputDisable || (content.label.includes("edit") && content.formData.implement_interim_containment_action !== null) || (content.label === 'edit' && content.userData?.roleCode !== 'CLTADM')}
                          value="No"
                          control={<Radio />}
                          label="No" />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2}>
                {/* <Grid item >
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 4 }}>
                    <NCRFormLabel isRequired={true} sx={{ pr: 3 }}>Severity of NC:</NCRFormLabel>
                    <NCRSelectField
                      name="severity_or_priority_level"
                      value={content.formData.severity_or_priority_level}
                      onChange={handleInputChange}
                      options={content.lookupTypes?.severity_or_priority_level || []}
                      disabled={content.inputDisable || (content.label === 'edit' && content.userData?.roleCode !== 'CLTADM')}
                      error={!!content?.errors?.severity_or_priority_level}
                    />
                  </Box>
                </Grid> */}
                <Grid item >
                  <Box sx={{ my: 2, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <NCRFormLabel isRequired={true} sx={{ pr: 5 }}>Responsible Team/<br /> Person:</NCRFormLabel>
                    <NCRSelectField
                      name="responsible_team_or_person"
                      value={content.formData.responsible_team_or_person}
                      onChange={handleInputChange}
                      options={content.lookupTypes?.responsibleTeam || []}
                      disabled={content.inputDisable}
                      error={!!content?.errors?.responsible_team_or_person}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2}>
                <Grid item >
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <NCRFormLabel sx={{ pr: 3 }}>Approving Authority:</NCRFormLabel>
                    <TextField
                      size='small'
                      sx={{
                        width: '190px',
                      }}
                      name='approving_authority'
                      onChange={handleInputChange}
                      value={content.formData.approving_authority || ''}
                      disabled={content.inputDisable || content.label.includes("initiate") || content.label.includes("edit")}
                      placeholder="Enter Reported by"
                    />
                  </Box>
                </Grid>
                <Grid item >
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <NCRFormLabel sx={{ pr: 14 }}>Status:</NCRFormLabel>
                    <TextField
                      size='small'
                      sx={{
                        width: '190px',
                      }}
                      name='status'
                      onChange={handleInputChange}
                      value={content.formData.status || 'Open'}
                      disabled={content.inputDisable || content.label.includes("initiate") || content.label.includes("edit")}
                      placeholder="Enter Reported by"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </ThemeProvider >
    </>
  );
};